import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const ConfirmCancellation: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  // Get email from URL parameters if provided
  const emailParam = searchParams.get('email');

  const content = {
    en: {
      title: "Confirm Appointment Cancellation",
      subtitle: "Please provide your appointment details to confirm cancellation",
      form: {
        email: "Email Address",
        emailPlaceholder: "Enter your email address",
        date: "Appointment Date",
        time: "Appointment Time",
        submit: "Confirm Cancellation"
      },
      success: {
        title: "Appointment Cancelled",
        message: "Your appointment has been successfully cancelled."
      },
      error: {
        required: "All fields are required",
        invalidEmail: "Please enter a valid email address",
        generic: "Something went wrong. Please try again."
      }
    },
    da: {
      title: "Bekræft Aflysning af Aftale",
      subtitle: "Angiv venligst dine aftaledetaljer for at bekræfte aflysningen",
      form: {
        email: "E-mailadresse",
        emailPlaceholder: "Indtast din e-mailadresse",
        date: "Aftaledato",
        time: "Aftaletid",
        submit: "Bekræft Aflysning"
      },
      success: {
        title: "Aftale Aflyst",
        message: "Din aftale er blevet aflyst."
      },
      error: {
        required: "Alle felter skal udfyldes",
        invalidEmail: "Indtast venligst en gyldig e-mailadresse",
        generic: "Noget gik galt. Prøv venligst igen."
      }
    }
  };

  useEffect(() => {
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [emailParam]);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleConfirmCancellation = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    if (!email.trim() || !selectedDate || !selectedTime) {
      setError(content[language as 'en' | 'da'].error.required);
      setIsSubmitting(false);
      return;
    }

    if (!validateEmail(email.trim())) {
      setError(content[language as 'en' | 'da'].error.invalidEmail);
      setIsSubmitting(false);
      return;
    }

    try {
      // Trigger Make.com webhook for cancellation confirmation
      const webhookPayload = {
        email: email.trim(),
        date: selectedDate,
        time: selectedTime,
        request_type: 'cancellation_confirmed'
      };

      console.log('Triggering cancellation confirmation webhook:', webhookPayload);

      const webhookResponse = await fetch('https://hook.eu2.make.com/gpj1w12sjg5baooxevb2mbwrhzp25o5t', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload),
      });

      if (!webhookResponse.ok) {
        console.error('Webhook failed:', webhookResponse.status, webhookResponse.statusText);
        throw new Error('Failed to process cancellation');
      }

      console.log('Cancellation confirmed successfully');
      setIsConfirmed(true);
      
      // Redirect to success page after 3 seconds
      setTimeout(() => {
        navigate('/success', { 
          state: { 
            type: 'cancellation',
            appointment: {
              email_address: email.trim(),
              appointment_date: selectedDate,
              appointment_time: selectedTime,
              status: 'cancelled'
            }
          } 
        });
      }, 3000);

    } catch (err) {
      console.error('Error confirming cancellation:', err);
      setError(content[language as 'en' | 'da'].error.generic);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isConfirmed) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h1
                className="text-3xl md:text-4xl font-serif font-bold mb-4 text-white"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {content[language as 'en' | 'da'].success.title}
              </h1>
              
              <p className="text-lg text-gray-300 mb-8">
                {content[language as 'en' | 'da'].success.message}
              </p>

              <p className="text-sm text-gray-400">
                Redirecting to homepage in 3 seconds...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] pt-32 pb-20">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h1
              className="text-3xl md:text-4xl font-serif font-bold mb-4 text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {content[language as 'en' | 'da'].title}
            </h1>
            <p className="text-lg text-gray-400">
              {content[language as 'en' | 'da'].subtitle}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-8">
            <form onSubmit={handleConfirmCancellation} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-500 text-sm">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  {content[language as 'en' | 'da'].form.email}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={content[language as 'en' | 'da'].form.emailPlaceholder}
                  className="w-full px-4 py-3 rounded-lg bg-[#2A2A2A]/50 border border-white/10 text-gray-400 placeholder-gray-500 cursor-not-allowed opacity-60"
                  required
                  disabled
                  readOnly
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-white mb-2">
                    {content[language as 'en' | 'da'].form.date}
                  </label>
                  <input
                    type="date"
                    id="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-[#2A2A2A] border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C]"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="time" className="block text-sm font-medium text-white mb-2">
                    {content[language as 'en' | 'da'].form.time}
                  </label>
                  <input
                    type="time"
                    id="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-[#2A2A2A] border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C]"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-all duration-300 disabled:opacity-70 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </>
                ) : content[language as 'en' | 'da'].form.submit}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmCancellation;